import * as React from 'react';
import { ComponentProps } from 'react';
import { ExpandMoreIcon } from '@/components/ui-parts/Icon';
import { cn } from '@/lib/utils';
import * as SelectPrimitive from '@radix-ui/react-select';
import { Button } from '../default/button';
import { Select, SelectContent, SelectGroup, SelectTrigger, SelectValue } from '../default/select';

type CustomSelectProps = {
  options: {
    label: string;
    value: string;
  }[];
  defaultValue?: string;
  placeholder?: string;
  onValueChange: (value: string) => void;
  className?: string;
} & ComponentProps<typeof Select>;

export const CustomSelect = ({ options, placeholder, defaultValue, onValueChange, ...props }: CustomSelectProps) => {
  return (
    <Select {...props} defaultValue={defaultValue} onValueChange={onValueChange}>
      <SelectTrigger
        className={cn(
          'border-shade-light-default bg-shade-white-default text-shade-dark-default relative flex h-8 w-full rounded border px-2 py-1 data-[placeholder]:text-[#B7C9C9]',
          // SelectTriggerの子要素のdefaultのsvgを非表示にする
          '[&>svg]:hidden',
          props.className,
        )}
      >
        <SelectValue placeholder={placeholder} className="text-base font-normal" />
        <Button
          asChild
          className="bg-shade-white-default border-shade-light-default absolute right-0 flex h-6 w-8 items-center justify-center rounded-l-none border-0 border-l px-1 py-0"
        >
          <ExpandMoreIcon size={24} />
        </Button>
      </SelectTrigger>
      <SelectContent className="bg-shade-white-default border-shade-light-default w-fit">
        <SelectGroup>
          {options.map((item) => (
            <SelectItem
              key={item.value}
              value={item.value}
              className={cn(
                'bg-shade-white-default cursor-pointer px-2',
                defaultValue === item.value && 'bg-[#F3F9FE]',
              )}
            >
              {item.label}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
};

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      'focus:bg-accent focus:text-accent-foreground relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      className,
    )}
    {...props}
  >
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;
